import { ErrorAlert } from "#components/ErrorAlert"
import { Button } from "#components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "#components/ui/card"
import { useAuth } from "#contexts/AuthContext"
import { useI18n } from "#hooks/use-i18n"
import { cn } from "#lib/utils"
import type { AuthenticateUser } from "@home-finance-manager/shared/types/auth"
import { createAuthenticateUserValidator } from "@home-finance-manager/shared/validators/auth.validator.factory"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useMemo } from "react"
import { useForm } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"
import { FormDivider } from "./forms/FormDivider"
import { FormField } from "./forms/FormField"
import { SocialLoginButtons } from "./forms/SocialLoginButtons"
import { TermsFooter } from "./forms/TermsFooter"

type LoginFormData = AuthenticateUser

interface LoginFormProps {
  onSwitchToRegister: () => void
  className?: string
}

export function LoginForm({ onSwitchToRegister, className }: LoginFormProps) {
  const navigate = useNavigate()
  const { login, isLoading, error, isAuthenticated } = useAuth()
  const { t } = useI18n()

  const loginSchema = useMemo(() => createAuthenticateUserValidator(t), [t])

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  })

  const emailValue = watch("email")
  const isEmailNotVerifiedError =
    error?.toLowerCase().includes("verify") || error?.toLowerCase().includes("verification")

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true })
    }
  }, [isAuthenticated, isLoading])

  const passwordLabelAction = (
    <a href="#" className="ml-auto text-sm underline-offset-4 hover:underline">
      {t("components.forms.forgotPassword")}
    </a>
  )

  const onSubmit = async (data: LoginFormData) => {
    if (!data.email || !data.password) {
      return
    }
    await login(data.email, data.password)
  }

  return (
    <div className={cn("max-w-md w-full", className)}>
      <Card className="">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">{t("components.login.title")}</CardTitle>
          <CardDescription>{t("components.login.socialLogin")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid gap-6">
              <SocialLoginButtons
                onAppleLogin={() => {
                  console.log("Login with Apple")
                }}
                onGoogleLogin={() => {
                  console.log("Login with Google")
                }}
                appleText={t("components.login.apple")}
                googleText={t("components.login.google")}
              />
              <FormDivider />
              <div className="grid gap-6">
                <FormField
                  name="email"
                  control={control}
                  label={t("components.forms.email")}
                  type="email"
                  placeholder="m@example.com"
                  error={errors.email}
                />
                <FormField
                  name="password"
                  control={control}
                  label={t("components.forms.password")}
                  type="password"
                  error={errors.password}
                  labelAction={passwordLabelAction}
                />
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? t("components.forms.loading") : t("components.forms.login")}
                </Button>
                <ErrorAlert error={error} className="mt-4" />
                {isEmailNotVerifiedError && (
                  <div className="mt-2 text-center">
                    <Link
                      to={`/verify-email${emailValue ? `?email=${encodeURIComponent(emailValue)}` : ""}`}
                      className="text-sm text-primary underline underline-offset-4 hover:text-primary/80"
                    >
                      {t("components.login.resendVerificationEmail")}
                    </Link>
                  </div>
                )}
              </div>
              <div className="text-center text-sm">
                {t("components.forms.dontHaveAccount")}{" "}
                <button
                  type="button"
                  onClick={onSwitchToRegister}
                  className="underline underline-offset-4 hover:text-primary"
                >
                  {t("components.forms.signUp")}
                </button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
      <TermsFooter />
    </div>
  )
}
